import React, { useEffect, useMemo, useState, useCallback } from "react";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import UserFormDialog from "../components/UserFormDialog";
import PasswordDisplayDialog from "../components/PasswordDisplayDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import UserSchedaSheet from "../components/UserSchedaSheet";
import OrgFormDialog from "../components/OrgFormDialog";
import OrderAssistDialog from "../components/OrderAssistDialog";
import NewOrgDialog from "../components/NewOrgDialog";
import NewLearnerDialog from "../components/NewLearnerDialog";
import LearnerEditDialog from "../components/LearnerEditDialog";
import {
  Users,
  Plus,
  Search,
  UserRoundPen,
  KeyRound,
  ShieldOff,
  ShieldCheck,
  Loader2,
  ChevronRight,
  ChevronDown,
  Building2,
  User,
  GraduationCap,
} from "lucide-react";
import { Input } from "../components/ui/input";

// Segmenti della rubrica. "tutti" mostra i clienti (aziende + privati); i discenti
// sono raggiungibili espandendo l'azienda di appartenenza. Lo staff interno
// (super_admin/formatore) vive in un tab separato.
const TABS = [
  { key: "tutti", label: "Tutti" },
  { key: "aziende", label: "Aziende" },
  { key: "discenti", label: "Discenti" },
  { key: "privati", label: "Privati" },
  { key: "staff", label: "Staff" },
];

const typeMeta = {
  azienda: { label: "Azienda", cls: "bg-brand-blue/10 text-brand-blue border-brand-blue/25" },
  discente: { label: "Discente", cls: "bg-brand-gold/15 text-brand-gold-deep border-brand-gold/40" },
  privato: { label: "Privato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  super_admin: { label: "Super Admin", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  formatore: { label: "Formatore", cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

function classify(u) {
  if (u.role === "super_admin" || u.role === "formatore") return u.role;
  if (u.role === "azienda") return "azienda";
  if (u.role === "studente" && u.organization_id) return "discente";
  return "privato";
}

function initials(u) {
  const a = (u.first_name || "").trim();
  const b = (u.last_name || "").trim();
  if (a || b) return ((a[0] || "") + (b[0] || "")).toUpperCase();
  return (u.email || u.username || "?").slice(0, 2).toUpperCase();
}

const avatarStyle = {
  azienda: "bg-brand-blue/10 text-brand-blue",
  discente: "bg-brand-gold/15 text-brand-gold-deep",
  privato: "bg-emerald-50 text-emerald-700",
  super_admin: "bg-slate-100 text-slate-600",
  formatore: "bg-violet-50 text-violet-700",
};

export default function AdminUtenti() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tutti");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createRole, setCreateRole] = useState("studente"); // ruolo del "Nuovo …" da creare
  const [pwDialog, setPwDialog] = useState({ open: false, payload: null });

  const openCreate = (role) => { setCreateRole(role); setEditing(null); setFormOpen(true); };
  const [confirm, setConfirm] = useState({ open: false, title: "", description: "", onConfirm: null, destructive: false });

  // Scheda cliente (pannello laterale) + modifica dati azienda.
  const [schedaId, setSchedaId] = useState(null);
  const [schedaOpen, setSchedaOpen] = useState(false);
  const [orgEditing, setOrgEditing] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const bump = () => setReloadToken((t) => t + 1);

  // Assistenza acquisto + creazione azienda.
  const [orderAssist, setOrderAssist] = useState({ open: false, buyer: null, learners: [] });
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const [newLearnerOrg, setNewLearnerOrg] = useState(null);
  const [learnerEditing, setLearnerEditing] = useState(null);

  const openScheda = (u) => { setSchedaId(u.id); setSchedaOpen(true); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // L'endpoint /admin/users accetta al massimo limit=200: pagino per
      // raccogliere tutti gli utenti senza superare il tetto (evita il 422).
      const pageSize = 200;
      let all = [];
      let skip = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await api.get("/admin/users", { params: { limit: pageSize, skip } });
        const batch = data.items || [];
        all = all.concat(batch);
        const total = typeof data.total === "number" ? data.total : all.length;
        if (batch.length < pageSize || all.length >= total) break;
        skip += pageSize;
      }
      const oRes = await api.get("/admin/organizations");
      // Dedup per id: difesa contro eventuali duplicati dalla paginazione
      // (chiavi React duplicate romperebbero il rendering della lista).
      const byId = new Map();
      for (const u of all) byId.set(u.id, u);
      setUsers([...byId.values()]);
      setOrgs(oRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Indicizzazioni: org per id, discenti per organization_id.
  const orgById = useMemo(() => {
    const m = {};
    for (const o of orgs) m[o.id] = o;
    return m;
  }, [orgs]);

  const learnersByOrg = useMemo(() => {
    const m = {};
    for (const u of users) {
      if (u.role === "studente" && u.organization_id) {
        (m[u.organization_id] ||= []).push(u);
      }
    }
    return m;
  }, [users]);

  const counts = useMemo(() => {
    const c = { tutti: 0, aziende: 0, discenti: 0, privati: 0, staff: 0 };
    for (const u of users) {
      const t = classify(u);
      if (t === "azienda") { c.aziende++; c.tutti++; }
      else if (t === "discente") c.discenti++;
      else if (t === "privato") { c.privati++; c.tutti++; }
      else c.staff++;
    }
    return c;
  }, [users]);

  // Testo cercabile per un utente (include i dati dell'azienda collegata).
  const haystack = useCallback((u) => {
    const org = u.organization_id ? orgById[u.organization_id] : null;
    return [
      u.first_name, u.last_name, u.email, u.username, u.fiscal_code,
      org?.ragione_sociale, org?.vat_number, org?.tax_code, org?.pec,
    ].filter(Boolean).join(" ").toLowerCase();
  }, [orgById]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (u) => !q || haystack(u).includes(q);
    let list;
    if (tab === "aziende") list = users.filter((u) => classify(u) === "azienda");
    else if (tab === "discenti") list = users.filter((u) => classify(u) === "discente");
    else if (tab === "privati") list = users.filter((u) => classify(u) === "privato");
    else if (tab === "staff") list = users.filter((u) => ["super_admin", "formatore"].includes(classify(u)));
    else list = users.filter((u) => ["azienda", "privato"].includes(classify(u)));
    return list.filter(match).sort((a, b) => (a.last_name || a.email || "").localeCompare(b.last_name || b.email || ""));
  }, [users, tab, search, haystack]);

  const orgLabel = (u) => {
    const org = u.organization_id ? orgById[u.organization_id] : null;
    return org?.ragione_sociale || `${u.first_name} ${u.last_name}`.trim();
  };

  const subline = (u, type) => {
    const org = u.organization_id ? orgById[u.organization_id] : null;
    if (type === "azienda") {
      const n = (learnersByOrg[u.organization_id] || []).length;
      const piva = org?.vat_number || org?.tax_code;
      return [piva && `P.IVA ${piva}`, `${n} discent${n === 1 ? "e" : "i"}`].filter(Boolean).join(" · ");
    }
    if (type === "discente") {
      return [u.fiscal_code, org?.ragione_sociale].filter(Boolean).join(" · ");
    }
    return u.email || u.username || "—";
  };

  // --- Azioni account (riuso della logica di AdminUsers) ---
  const handleResetPassword = (u) => {
    setConfirm({
      open: true,
      title: `Reset password di ${u.email || u.username}?`,
      description: "Verrà generata una nuova password casuale mostrata una sola volta.",
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, open: false }));
        try {
          const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
          setPwDialog({
            open: true,
            payload: {
              email: u.email || u.username,
              password: data.generated_password,
              title: "Password resettata",
              subtitle: `Nuova password per ${u.email || u.username}. Comunicala all’utente in modo sicuro.`,
            },
          });
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
  };

  const handleToggleActive = (u) => {
    const action = u.is_active ? "deactivate" : "activate";
    const verb = u.is_active ? "disattivare" : "attivare";
    setConfirm({
      open: true,
      title: `${verb.charAt(0).toUpperCase() + verb.slice(1)} ${u.email || u.username}?`,
      description: u.is_active
        ? "L’utente non potrà più accedere alla piattaforma."
        : "L’utente potrà nuovamente accedere alla piattaforma.",
      destructive: u.is_active,
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, open: false }));
        try {
          const { data } = await api.post(`/admin/users/${u.id}/${action}`);
          setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
          bump();
          toast.success(u.is_active ? "Utente disattivato" : "Utente attivato");
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });
  };

  const deleteUserFlow = (u, closeSheet) => {
    const name = u.email || u.username || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "l'utente";
    setConfirm({
      open: true,
      destructive: true,
      title: `Eliminare ${name}?`,
      description: "Operazione definitiva: rimuove l'utente e i dati collegati non protetti. Se ha ordini pagati o attestati, usa invece Sospendi.",
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, open: false }));
        try {
          await api.delete(`/admin/users/${u.id}`);
          toast.success("Utente eliminato.");
          if (closeSheet) setSchedaOpen(false);
          load(); bump();
        } catch (e) { toast.error(formatApiError(e)); }
      },
    });
  };

  const deleteOrgFlow = (org) => {
    setConfirm({
      open: true,
      destructive: true,
      title: `Eliminare l'azienda ${org.ragione_sociale}?`,
      description: "Operazione definitiva: rimuove account, discenti, licenze e ordini non pagati. Se ha ordini pagati o attestati emessi, usa invece Sospendi.",
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, open: false }));
        try {
          await api.delete(`/admin/organizations/${org.id}`);
          toast.success("Azienda eliminata.");
          setSchedaOpen(false);
          load(); bump();
        } catch (e) { toast.error(formatApiError(e)); }
      },
    });
  };

  const renderActions = (u, canEdit) => (
    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
      {canEdit && (
        <button
          title="Modifica"
          onClick={() => { setEditing(u); setFormOpen(true); }}
          className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted transition-colors"
        >
          <UserRoundPen size={16} />
        </button>
      )}
      <button
        title="Reset password"
        onClick={() => handleResetPassword(u)}
        className="p-1.5 rounded-sm border border-brand-line hover:border-brand-gold hover:text-brand-gold text-brand-muted transition-colors"
      >
        <KeyRound size={16} />
      </button>
      <button
        title={u.is_active ? "Disattiva" : "Attiva"}
        onClick={() => handleToggleActive(u)}
        disabled={u.id === user?.id}
        className="p-1.5 rounded-sm border border-brand-line hover:border-red-500 hover:text-red-600 text-brand-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {u.is_active ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
      </button>
    </div>
  );

  const renderRow = (u, child = false) => {
    const type = classify(u);
    const meta = typeMeta[type];
    const canExpand = type === "azienda" && (learnersByOrg[u.organization_id] || []).length > 0;
    const isOpen = expanded[u.id];
    const name = type === "azienda" ? orgLabel(u) : `${u.first_name} ${u.last_name}`.trim();
    return (
      <React.Fragment key={u.id}>
        <div
          onClick={() => openScheda(u)}
          className={`flex items-center gap-3 px-4 py-3 border-b border-brand-line last:border-b-0 hover:bg-brand-bg transition-colors cursor-pointer ${child ? "pl-12 bg-brand-bg/40" : ""}`}
        >
          {child ? (
            <GraduationCap size={15} className="text-brand-muted shrink-0" />
          ) : canExpand ? (
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded((ex) => ({ ...ex, [u.id]: !ex[u.id] })); }}
              className="text-brand-muted hover:text-brand-ink shrink-0"
              title={isOpen ? "Comprimi" : "Espandi discenti"}
            >
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}

          <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold shrink-0 ${avatarStyle[child ? "discente" : type]}`}>
            {initials(u)}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-brand-ink truncate">{name}</span>
              {!u.is_active && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 shrink-0">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Disattivato
                </span>
              )}
            </div>
            <div className="text-xs text-brand-muted truncate">{subline(u, child ? "discente" : type)}</div>
          </div>

          {!child && meta && (
            <span className={`hidden sm:inline-flex text-xs font-semibold px-2 py-0.5 rounded-sm border ${meta.cls} shrink-0`}>
              {meta.label}
            </span>
          )}

          {renderActions(u, type !== "azienda")}
        </div>

        {canExpand && isOpen &&
          (learnersByOrg[u.organization_id] || []).map((d) => renderRow(d, true))}
      </React.Fragment>
    );
  };

  return (
    <div data-testid="admin-utenti-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
              <Users size={14} /> Pannello Super Admin
            </div>
            <h1 className="text-3xl sm:text-4xl text-brand-ink mb-1">Utenti</h1>
            <p className="text-sm text-brand-muted">
              Aziende, discenti e privati in un'unica rubrica
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="new-org-btn"
              onClick={() => setNewOrgOpen(true)}
              className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm"
            >
              <Building2 size={16} /> Nuova azienda
            </button>
            <button
              data-testid="new-formatore-btn"
              onClick={() => openCreate("formatore")}
              className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm"
            >
              <GraduationCap size={16} /> Nuovo formatore
            </button>
            <button
              data-testid="new-admin-btn"
              onClick={() => openCreate("super_admin")}
              className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm"
            >
              <ShieldCheck size={16} /> Nuovo admin
            </button>
            <button
              data-testid="new-user-btn"
              onClick={() => openCreate("studente")}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={16} /> Nuovo privato
            </button>
          </div>
        </div>

        {/* Ricerca */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input
            data-testid="utenti-search"
            placeholder="Cerca per nome, email, codice fiscale, ragione sociale, P.IVA…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded-sm"
          />
        </div>

        {/* Tab segmenti */}
        <div className="flex flex-wrap gap-2 mb-5">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`utenti-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={
                  "inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors " +
                  (active
                    ? "bg-brand-blue text-white border-brand-blue"
                    : "bg-white text-brand-muted border-brand-line hover:border-brand-blue/40")
                }
              >
                {t.label}
                <span className={active ? "text-white/70" : "text-brand-muted/60"}>{counts[t.key]}</span>
              </button>
            );
          })}
        </div>

        {/* Elenco */}
        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-brand-muted">
              <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-brand-muted text-sm">
              {tab === "staff" ? (
                <><User size={20} className="inline mr-2 opacity-60" /> Nessun membro dello staff.</>
              ) : tab === "aziende" ? (
                <><Building2 size={20} className="inline mr-2 opacity-60" /> Nessuna azienda trovata.</>
              ) : (
                "Nessun utente trovato con i filtri correnti."
              )}
            </div>
          ) : (
            rows.map((u) => renderRow(u))
          )}
        </div>
        <div className="text-xs text-brand-muted mt-3 flex items-center gap-1.5">
          <ChevronRight size={13} /> Espandi un'azienda per vedere i suoi discenti.
        </div>
      </main>

      <UserSchedaSheet
        userId={schedaId}
        open={schedaOpen}
        onOpenChange={setSchedaOpen}
        reloadToken={reloadToken}
        onEditUser={(u) => { setEditing(u); setFormOpen(true); }}
        onEditOrg={(org) => setOrgEditing(org)}
        onResetPassword={handleResetPassword}
        onToggleActive={handleToggleActive}
        onCreateOrder={({ buyer, learners }) => setOrderAssist({ open: true, buyer, learners: learners || [] })}
        onAddLearner={(org) => setNewLearnerOrg(org)}
        onEditLearner={(l) => setLearnerEditing(l)}
        onDeleteLearner={(l) => deleteUserFlow(l, false)}
        onDeleteUser={(u) => deleteUserFlow(u, true)}
        onDeleteOrg={(org) => deleteOrgFlow(org)}
        onChanged={() => { load(); bump(); }}
      />

      <OrderAssistDialog
        open={orderAssist.open}
        onOpenChange={(v) => setOrderAssist((s) => ({ ...s, open: v }))}
        buyer={orderAssist.buyer}
        learners={orderAssist.learners}
        onDone={() => bump()}
      />

      <NewOrgDialog
        open={newOrgOpen}
        onOpenChange={setNewOrgOpen}
        onCreated={() => { load(); bump(); }}
        onGeneratedPassword={(payload) => setPwDialog({ open: true, payload })}
      />

      <NewLearnerDialog
        org={newLearnerOrg}
        onOpenChange={(v) => !v && setNewLearnerOrg(null)}
        onCreated={() => { load(); bump(); }}
      />

      <LearnerEditDialog
        learner={learnerEditing}
        onOpenChange={(v) => !v && setLearnerEditing(null)}
        onSaved={() => { load(); bump(); }}
      />

      <OrgFormDialog
        open={!!orgEditing}
        onOpenChange={(v) => !v && setOrgEditing(null)}
        org={orgEditing}
        onSaved={() => { load(); bump(); }}
      />

      <UserFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        fixedRole={createRole}
        onCreated={() => { load(); bump(); }}
        onUpdated={() => { load(); bump(); }}
        onGeneratedPassword={(payload) => setPwDialog({ open: true, payload })}
      />

      <PasswordDisplayDialog
        open={pwDialog.open}
        onOpenChange={(v) => setPwDialog({ open: v, payload: v ? pwDialog.payload : null })}
        payload={pwDialog.payload}
      />

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(v) => setConfirm((c) => ({ ...c, open: v }))}
        title={confirm.title}
        description={confirm.description}
        destructive={confirm.destructive}
        onConfirm={() => confirm.onConfirm?.()}
        testid="admin-utenti-confirm"
      />
    </div>
  );
}
